import type { ElementType, JSX, ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDayMonth } from '../format';
import {
  CHRONICLE,
  CHRONICLE_PROVENANCE,
  chronicleCount,
  METHODOLOGY_LINK_LABEL,
  METHODOLOGY_PATH,
  SOURCE_LABELS,
} from '../labels';
import type { Chronicle } from '../useChronicle';
import { ChronicleItem } from './ChronicleItem';
import { ErrorNote } from './ErrorNote';
import { InfoPopover } from './InfoPopover';
import { Skeleton } from './ui/skeleton';

/**
 * The chronicle panel, shared by the landing rail and the site-wide Sheet (ux-spec §4).
 *
 * It owns the honest states as well as the list: the day it names, the sentence each series owes
 * about that day, and the one provenance line standing for both.
 */

/** The mockup's empty-state mark — a road sign, one of the themed icons the livery rules allow. */
function EmptyMark(): JSX.Element {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
      <title>Tühi ööpäev</title>
      <path d="M17 4 24 26H10L17 4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <line
        x1="6"
        y1="29"
        x2="28"
        y2="29"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line x1="13.4" y1="16" x2="20.6" y2="16" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** Placeholders roughly the size and number of the entries that replace them, so the panel does not
 * grow by a screenful when the day lands. */
const SKELETON_ROWS = [1, 2, 3, 4, 5];
const NOTE_LINES = [1, 2, 3];

/** The rail shows the top of the window and sends the rest to the Sheet; the Sheet passes no limit. */
export interface ChronicleFeedProps {
  chronicle: Chronicle;
  limit?: number;
  footer?: ReactNode;
  /** The Sheet passes its `SheetTitle` so the dialog's title and the visible heading are one node. */
  titleAs?: ElementType;
}

export function ChronicleFeed({
  chronicle,
  limit,
  footer,
  titleAs: Title = 'h2',
}: ChronicleFeedProps): JSX.Element {
  const { day, entries, count, isBehind, notes, isPending, isError, error } = chronicle;
  const shown = limit === undefined ? entries : entries.slice(0, limit);
  // The extension can widen the window past the day the header names; when it has, every row
  // carries its own day so none of them can pass for the named one.
  const showDay = chronicle.window !== null && chronicle.window.from !== chronicle.window.to;

  return (
    <div className="min-w-0">
      <Title className="m-0 font-display text-h3 font-bold tracking-tight text-accent-deep">
        {CHRONICLE.title}
      </Title>

      {/* The chip's slot is held open from the first paint: it sits above the whole list. */}
      <div className="min-h-[1.875rem]">
        {day !== null && (
          <span className="mt-1.5 mb-0.5 inline-block rounded-full bg-accent-wash px-2 py-0.5 text-caption font-semibold text-accent-deep">
            {CHRONICLE.day} · {formatDayMonth(day)}
            {isBehind && ` · ${CHRONICLE.lastProcessed}`}
          </span>
        )}
      </div>

      <p className="mt-0.5 mb-2 text-caption text-ink-muted">
        {count !== null && `${chronicleCount(count)} · `}
        {CHRONICLE.zone}
      </p>

      {/* Each series' own sentence about this window, so a series that has not been read this far
          is never mistaken for a night on which nothing happened. */}
      {notes.map((note) => (
        <p key={note.source} className="mb-2 text-caption leading-normal text-ink-secondary">
          <span className="font-semibold">{SOURCE_LABELS[note.source]}:</span> {note.note}
        </p>
      ))}

      {/* And the sentence each series owes, which is three lines of caveat more often than not. */}
      {isPending && (
        <div className="mb-2 flex flex-col gap-1.5" aria-hidden="true">
          {NOTE_LINES.map((line) => (
            <Skeleton key={line} className={line === 3 ? 'h-3 w-2/3' : 'h-3'} />
          ))}
        </div>
      )}

      {isError && <ErrorNote error={error} what="Kroonika" />}

      {isPending && (
        <div className="flex flex-col gap-2" role="status" aria-label={CHRONICLE.loading}>
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} className="h-28" />
          ))}
        </div>
      )}

      {/* The affirmative sentence, and the only place it may appear: a day with no rows and no
          series owing a caveat is a day the police described nothing on. */}
      {!isPending && !isError && entries.length === 0 && notes.length === 0 && (
        <div className="flex flex-col items-center gap-2 px-4 py-6 text-center text-ink-muted">
          <EmptyMark />
          <p className="m-0 max-w-[17rem] text-small text-ink-secondary">{CHRONICLE.empty}</p>
        </div>
      )}

      {shown.length > 0 && (
        <ol className="m-0 list-none p-0">
          {shown.map((entry) => (
            <ChronicleItem
              key={`${entry.source}-${entry.item.id}`}
              entry={entry}
              showDay={showDay}
            />
          ))}
        </ol>
      )}

      {footer}

      <p className="mt-2.5 mb-0 text-caption leading-normal text-ink-muted">
        {CHRONICLE_PROVENANCE.short}
        <InfoPopover label="Kust kroonika read tulevad">{CHRONICLE_PROVENANCE.full}</InfoPopover>
        <Link to={METHODOLOGY_PATH} className="text-accent hover:text-accent-deep">
          {METHODOLOGY_LINK_LABEL}
        </Link>
      </p>
    </div>
  );
}
